
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { roomsData } from '../../../mocks/rooms';

export default function RoomDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const currentLang = i18n.language as 'ja' | 'en' | 'zh';
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const room = roomsData.find(r => r.id === parseInt(id || '0'));

  if (!room) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">お部屋が見つかりません</h1>
            <Link to="/rooms" className="text-blue-600 hover:text-blue-700 cursor-pointer">
              お部屋一覧に戻る
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-700 cursor-pointer">
              {t('nav_home')}
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/rooms" className="text-gray-500 hover:text-gray-700 cursor-pointer">
              {t('nav_rooms')}
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900">{room.title[currentLang]}</span>
          </nav>
        </div>
      </div>

      {/* Room Details */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div>
              <div className="relative mb-4">
                <img
                  src={room.images[currentImageIndex]}
                  alt={room.title[currentLang]}
                  className="w-full h-96 object-cover object-top rounded-lg"
                />
                {room.images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : room.images.length - 1)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex(currentImageIndex < room.images.length - 1 ? currentImageIndex + 1 : 0)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </>
                )}
              </div>
              
              {room.images.length > 1 && (
                <div className="flex space-x-2 overflow-x-auto">
                  {room.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer ${
                        currentImageIndex === index ? 'ring-2 ring-blue-600' : ''
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${room.title[currentLang]} ${index + 1}`}
                        className="w-full h-full object-cover object-top"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Room Info */}
            <div>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-3xl font-bold text-gray-900">{room.title[currentLang]}</h1>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                    {room.type[currentLang]}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-4 flex items-center text-lg">
                  <i className="ri-map-pin-line mr-2"></i>
                  {room.location[currentLang]}
                </p>

                <div className="flex items-center space-x-6 mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{room.size}</div>
                    <div className="text-sm text-gray-500">面積</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{t('ask_price')}</div>
                    <div className="text-sm text-gray-500">お気軽にお問い合わせください</div>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  {room.description[currentLang]}
                </p>

                {/* Amenities */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4">設備・サービス</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {room.amenities[currentLang].map((amenity, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <i className="ri-check-line text-green-600"></i>
                        <span className="text-sm text-gray-700">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact CTA */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">このお部屋にご興味がございますか？</h3>
                  <p className="text-gray-600 mb-4 text-sm">
                    詳細情報やご見学のご相談など、お気軽にお問い合わせください。
                  </p>
                  <Link
                    to="/contact"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap text-center block"
                  >
                    お問い合わせ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Rooms */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">その他のおすすめ物件</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {roomsData
              .filter(r => r.id !== room.id)
              .slice(0, 3)
              .map((relatedRoom) => (
                <div key={relatedRoom.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="relative h-48">
                    <img
                      src={relatedRoom.images[0]}
                      alt={relatedRoom.title[currentLang]}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-semibold text-blue-600">
                      {relatedRoom.type[currentLang]}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-lg font-semibold mb-2">{relatedRoom.title[currentLang]}</h3>
                    <p className="text-gray-600 mb-4 flex items-center text-sm">
                      <i className="ri-map-pin-line mr-2"></i>
                      {relatedRoom.location[currentLang]}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xl font-bold text-blue-600">{t('ask_price')}</span>
                        <div className="text-xs text-gray-500">
                          <div>{t('reference_price')}</div>
                          <div>¥{relatedRoom.price.toLocaleString()}</div>
                        </div>
                      </div>
                      <Link
                        to={`/rooms/${relatedRoom.id}`}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        詳細を見る
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
